import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Clock, CircleCheck, TriangleAlert } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { copyToClipboard } from '../utils/clipboard'
import { useShowHeaderIds } from '../hooks/useShowHeaderIds'

export interface StatsBarItem {
  label: string
  content: ReactNode
}

export interface StatsBarProps {
  items: StatsBarItem[]
}

export function StatsBar({ items }: StatsBarProps) {
  if (items.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 -mt-2.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-center empty:hidden">
          {item.content}
        </div>
      ))}
    </div>
  )
}

/* ─── Reusable sub-components for common column types ─────────────── */

export function StatsBarPacing({
  percent,
  delivered,
  total,
  unit = 'impressions',
}: {
  percent: number
  delivered: string
  total: string
  unit?: string
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-bold tabular-nums">{Math.round(percent)}%</span>
        <span className="text-xs text-muted-foreground">
          {t('stats.delivered')} &middot; {delivered} / {total} {unit}
        </span>
      </div>
      <div className="h-1 bg-muted rounded-full overflow-hidden w-24">
        <div
          className="h-full rounded-full bg-green-500 transition-[width] duration-300"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </div>
  )
}

/** Hidden unless the user turned on ids in their preferences; `always` overrides that. */
export function StatsBarId({ value, always = false }: { value: string; always?: boolean }) {
  const { t } = useTranslation()
  const { showIds } = useShowHeaderIds()
  if (!always && !showIds) return null
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-border bg-muted font-mono text-xs text-muted-foreground cursor-pointer hover:bg-border hover:text-foreground transition-colors"
      role="button"
      tabIndex={0}
      title={t('common.copyId')}
      onClick={() => copyToClipboard(value)}
      onKeyDown={(e) => { if (e.key === 'Enter') copyToClipboard(value) }}
    >
      {value}
      <Copy className="h-3 w-3" />
    </span>
  )
}

export function StatsBarSchedule({
  start,
  end,
  daysLeft,
}: {
  start: string
  end: string
  daysLeft?: number
}) {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      {start} &ndash; {end}
      {daysLeft != null && (
        <Badge variant="success">
          <Clock />
          {t('stats.daysLeft', { count: daysLeft })}
        </Badge>
      )}
    </span>
  )
}

export function StatsBarTraffic({ active }: { active: boolean }) {
  const { t } = useTranslation()
  return (
    <Badge variant={active ? 'success' : 'warning'}>
      {active ? <CircleCheck /> : <TriangleAlert />}
      {active ? t('stats.delivering') : t('stats.noRecentTraffic')}
    </Badge>
  )
}

export function StatsBarActivity({ created, updated }: { created: string; updated?: string }) {
  return (
    <span className="text-sm text-muted-foreground">
      Created <strong className="font-medium text-foreground">{created}</strong>
      {updated && <> &middot; Updated <strong className="font-medium text-foreground">{updated}</strong></>}
    </span>
  )
}
